import React, { useState, useEffect } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { useHistory, Link as NavLink } from 'react-router-dom';
import { DateTime } from 'luxon';

/// Scheduler Testing
import { ViewState } from '@devexpress/dx-react-scheduler';
import { Scheduler } from '@devexpress/dx-react-scheduler-material-ui';
import { MonthView } from '@devexpress/dx-react-scheduler-material-ui';
import { Appointments } from '@devexpress/dx-react-scheduler-material-ui';
import { Toolbar } from '@devexpress/dx-react-scheduler-material-ui';
import { DateNavigator } from '@devexpress/dx-react-scheduler-material-ui';
import { Resources } from '@devexpress/dx-react-scheduler-material-ui';
import { ViewSwitcher } from '@devexpress/dx-react-scheduler-material-ui';
import { WeekView } from '@devexpress/dx-react-scheduler-material-ui';
import { DayView } from '@devexpress/dx-react-scheduler-material-ui';
import { AllDayPanel } from '@devexpress/dx-react-scheduler-material-ui';
import { TodayButton } from '@devexpress/dx-react-scheduler-material-ui';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';

import Divider from '@material-ui/core/Divider';
import Tooltip from '@material-ui/core/Tooltip';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import RightIcon from '@material-ui/icons/ChevronRight';
import Chip from '@material-ui/core/Chip';
import TablePagination from '@material-ui/core/TablePagination';

import CalendarIcon from '@material-ui/icons/Today';
import ListIcon from '@material-ui/icons/List';
import AddIcon from '@material-ui/icons/Add';

import Loading from 'app/components/loaders/Loading';
import NewEventDialog from 'app/components/event/NewEventDialog';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';

import useApi from 'app/context/useApi';

const DayScaleCell = props => (
    <MonthView.DayScaleCell {...props} style={{ textAlign: 'center', fontWeight: 'bold' }} />
);

const styles = theme => ({
    appointment: {
        borderRadius: '4px',
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
        },
        backgroundColor: theme.palette.primary.main
    },
    apptContent: {
        '& > div > div': {
            whiteSpace: 'normal !important',
            lineHeight: 1.2,
            marginTop: 6,
        },
    },
});

const Appointment = ({ data, ...restProps }) => {

    const history = useHistory();
    const classes = useStyles();

    return (
        <Appointments.Appointment
            {...restProps}
            onClick={() => history.push(`/admin/events/${data.id}`)}
            className={classes.appointment}
        />
    )
};

const AppointmentContent = withStyles(styles, { name: 'AppointmentContent' })(({ classes, ...restProps }) => (
    <Appointments.AppointmentContent {...restProps} className={classes.apptContent} />
));

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3)
    },
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    marginRight: {
      marginRight: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    appointment: {
        borderRadius: '4px',
            '&:hover': {
                backgroundColor: theme.palette.primary.light,
            },
        backgroundColor: theme.palette.primary.main
    },
    newButton: {
        whiteSpace: 'nowrap'
    }
}));

export default function Events(props) {

    const classes = useStyles();
    const api = useApi();
    const history = useHistory();
    const now = DateTime.local().toSeconds()

    const [ newEventDialogOpen, setNewEventDialogOpen ] = useState(false);

    const [ events, setEvents ] = useState([])

    const [ loading, setLoading ] = useState(false);
    const [ currentView, setCurrentView ] = useState('Month');
    const [ viewType, setViewType ] = useState('list');
    const [ page, setPage ] = useState(0);
    const [ rowsPerPage, setRowsPerPage ] = useState(10);

    const parseEvents = initEvents => {

        let newEvents = [];

        initEvents.forEach(event => {

            let newEvent = {...event};

            newEvent.title = event.name;
            newEvent.startDate = event.starts_at;
            newEvent.endDate = event.ends_at;

            newEvents.push(newEvent)
        })

        setEvents(newEvents);
    }

    /// End Scheduler Testing

    const getEvents = () => {
        setLoading(true);

        api.get(`/admin/event`)
            .then(res => parseEvents(res.events))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        getEvents();
    }, []);

    // Handlers

    const handleTimeChange = (currentViewName) =>{
        setCurrentView(currentViewName && currentViewName.name)
    }

    useEffect(() => {
        handleTimeChange();
    }, [currentView]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(event.target.value)
    };

    // Dialog functions

    const openNewEventDialog = () => {
        setNewEventDialogOpen(true)
    }

    const closeNewEventDialog = () => {
        setNewEventDialogOpen(false);
    }

    return (
        <div className={classes.container}>
            <Loading isLoading={loading} />
            <Paper className={classes.paper}>
                <Grid
                    container
                    wrap="nowrap"
                    alignItems="center"
                    spacing={3}
                    className={classes.marginBottom}
                    >
                    <Grid item>
                        <Typography variant="h4" component="h2">Events</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Tooltip title="List View">
                            <IconButton
                                onClick={() => setViewType('list')}
                                color={viewType === 'list' ? 'primary' : 'default'}
                                >
                                <ListIcon/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Calendar View">
                            <IconButton
                                onClick={() => setViewType('calendar')}
                                color={viewType === 'calendar' ? 'primary' : 'default'}
                                className={classes.marginRight}
                                >
                                <CalendarIcon/>
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item>
                        <Button
                            color="primary"
                            onClick={openNewEventDialog}
                            variant="contained"
                            startIcon={<AddIcon />}
                            className={classes.newButton}
                            >
                            New Event
                        </Button>
                    </Grid>
                </Grid>
                <Divider />
                {viewType === 'calendar' &&
                    <Scheduler data={events}>
                        <ViewState
                            currentViewName={currentView}
                            onCurrentViewNameChange={handleTimeChange}
                            defaultCurrentDate={new Date()}
                        />
                        <MonthView dayScaleCellComponent={DayScaleCell}/>
                        <WeekView startDayHour={8}/>
                        <DayView />
                        <Appointments
                            appointmentComponent={Appointment}
                            appointmentContentComponent={AppointmentContent}
                        />
                        <Resources/>
                        <Toolbar />
                        <DateNavigator />
                        <TodayButton />
                        <ViewSwitcher />
                        <AllDayPanel />
                    </Scheduler>
                }
                {(viewType === 'list' && events.length > 0) && (
                    <>
                        <List>
                            {events.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(event => (
                                <ListItem
                                    key={event.id}
                                    button
                                    divider
                                    className={classes.listItem}
                                    to={`/admin/events/${event.id}`}
                                    component={NavLink}
                                    >
                                    {now < DateTime.fromISO(event.starts_at).toSeconds() &&
                                        <ListItemAvatar>
                                            <Chip
                                                className={classes.marginRight}
                                                label='Upcoming'
                                                color='primary'
                                            />
                                        </ListItemAvatar>
                                    }
                                    <ListItemText
                                        primary={event.name}
                                        primaryTypographyProps={{variant: "h6", color: (now > DateTime.fromISO(event.starts_at).toSeconds()) ? 'textSecondary' : 'primary'}}
                                        secondary={DateTime.fromISO(event.starts_at).toLocaleString(DateTime.DATETIME_FULL)}
                                        secondaryTypographyProps={{variant: "body1"}}
                                    />
                                    <ListItemSecondaryAction >
                                        <Tooltip title="View Details">
                                            <IconButton
                                                aria-label="edit"
                                                onClick={() => history.push(`/admin/events/${event.id}`)}
                                                >
                                                <RightIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            count={events.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onChangePage={handleChangePage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                            component="div"
                        />
                    </>
                )}
                {(viewType === 'list' && events.length === 0) && (
                    <Typography
                        align="center"
                        color="textSecondary"
                        variant="subtitle2"
                        className={classes.empty}
                        >
                        You have no events to display at this time.
                    </Typography>
                )}
            </Paper>
            <NewEventDialog
                open={newEventDialogOpen}
                onClose={closeNewEventDialog}
            />
        </div>
    );
}
